import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../api';
import './login.css';

const Login = ({ onLogin }: { onLogin: (user: any) => void }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  // Toggle to show admin code reset panel below the login form
  const [showAdminReset, setShowAdminReset] = useState(false);
  // Reset with admin-generated code
  const [rcEmail, setRcEmail] = useState('');
  const [rcCode, setRcCode] = useState('');
  const [rcNew, setRcNew] = useState('');
  const [rcConfirm, setRcConfirm] = useState('');
  const [rcLoading, setRcLoading] = useState(false);
  const [rcMessage, setRcMessage] = useState('');
  const [rcError, setRcError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);
    try {
      const normalizedEmail = (email || '').trim().toLowerCase();
      if (normalizedEmail !== email) {
        setInfo(`We normalized your email to ${normalizedEmail} (emails are case-insensitive).`);
      }
      const res = await api.post('/api/auth/login', { email: normalizedEmail, password });
      onLogin(res.data.user);
      localStorage.setItem('token', res.data.token);
      navigate('/my-expenses');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResetWithCode = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setRcError('');
    setRcMessage('');
    const emailVal = (rcEmail || email).trim();
    if (!emailVal || !rcCode || !rcNew) {
      setRcError('Email, code, and new password are required');
      return;
    }
    if (rcNew !== rcConfirm) {
      setRcError('Passwords do not match');
      return;
    }
    setRcLoading(true);
    try {
      const res = await api.post('/api/auth/reset-password-with-code', {
        email: emailVal,
        code: rcCode,
        newPassword: rcNew,
      });
      setRcMessage(res.data?.message || 'Password has been reset. You can now sign in.');
    } catch (err: any) {
      setRcError(err.response?.data?.message || 'Failed to reset password with code');
    } finally {
      setRcLoading(false);
    }
  };

  // Removed email link and token-based reset flows. Only admin-code reset is supported here.

  return (
    <div className="login-page">
      <form onSubmit={handleSubmit} className="login-card">
        <div className="login-card__header">
          <h2 className="login-card__title">Welcome back</h2>
          <p className="login-card__subtitle">Sign in to your account</p>
        </div>

        {error && (
          <div className="login-alert login-alert--error">
            {error}
          </div>
        )}
        {info && (
          <div className="login-alert login-alert--info">
            {info}
          </div>
        )}

        <div className="login-stack">
          <div>
            <label className="login-label">Email</label>
            <input
              value={email}
              onChange={e => setEmail(e.target.value)}
              type="email"
              required
              className="login-field"
            />
            {(email && (email !== (email.trim().toLowerCase()))) && (
              <div className="login-help">
                We will sign you in as <strong>{(email || '').trim().toLowerCase()}</strong> (emails are case-insensitive).
              </div>
            )}
          </div>
          <div>
            <label className="login-label">Password</label>
            <input
              value={password}
              onChange={e => setPassword(e.target.value)}
              type="password"
              required
              className="login-field"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="login-submit"
          >{loading ? 'Signing in…' : 'Sign in'}</button>
        </div>

        <p className="login-register">
          Don't have an account? <Link to="/register">Create one</Link>
        </p>
      </form>
      <div className="login-admin-toggle">
        <button
          type="button"
          onClick={() => setShowAdminReset(v => !v)}
          className="login-admin-toggle__btn"
        >{showAdminReset ? 'Hide admin code reset' : 'Reset with admin code'}</button>
      </div>
      {showAdminReset && (
        <div className="login-admin-card">
          <h3 className="login-admin-card__title">Reset with admin code</h3>
          {rcMessage && <div className="login-admin-alert login-admin-alert--success">{rcMessage}</div>}
          {rcError && <div className="login-admin-alert login-admin-alert--error">{rcError}</div>}
          <div className="login-admin-grid">
            <input
              type="email"
              placeholder="Your email"
              value={rcEmail || email}
              onChange={e => setRcEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
              className="login-admin-field"
            />
            <input
              type="text"
              placeholder="6-digit code"
              value={rcCode}
              onChange={e => setRcCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
              inputMode="numeric"
              pattern="\d{6}"
              title="Enter the 6-digit code provided by your admin"
              className="login-admin-field login-admin-field--code"
            />
            <input
              type="password"
              placeholder="New password (min 10 chars, upper/lower/number/symbol)"
              value={rcNew}
              onChange={e => setRcNew(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
              className="login-admin-field"
            />
            <input
              type="password"
              placeholder="Confirm new password"
              value={rcConfirm}
              onChange={e => setRcConfirm(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
              className="login-admin-field"
            />
            <button type="button" onClick={() => handleResetWithCode()} disabled={rcLoading} className="login-admin-button">{rcLoading ? 'Resetting…' : 'Reset with code'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;
